class AconSkinkoWishlist {
    constructor() {
        this.DEBUG = false;
        this.config = window.acon_skinko_config || {};
        this.queue = [];
        this.isSyncing = false;
        this.isSaving = false;
        this.retryCount = 0;
        this.maxRetries = 3;
        this.debounceTimer = null;
        this.debounceTime = 1000;
        this.wishlist = { folders: { default: [] } };
        this.productCache = {};
        this.currentProduct = null;
        this.wishlistProductSet = new Set();
        if (this.DEBUG) {
            console.group('[Wishlist] Constructor');
            console.log('Configuración:', JSON.stringify(this.config, null, 2));
            if (!this.config.jwtSecret) {
                console.warn('Advertencia: jwtSecret está vacío. Verifica shop.metafields.custom.jwt_secret en Shopify Liquid.');
            }
            console.groupEnd();
        }
        this.init();
    }

    generateJWT() {
        if (!this.config.jwtSecret) {
            throw new Error('jwtSecret no definido para generar JWT');
        }
        return this.config.jwtSecret;
    }

    init() {
        if (this.DEBUG) console.log('[Wishlist] Inicializando...');
        this.loadFromLocalStorage();
        this.cleanInvalidFolders();
        this.setupEventListeners();
        this.updateWishlistProductSet();
        this.updateAllButtons();
        this.syncWithServer();
        if (document.querySelector('.acon_skinko_wishlist_page')) {
            this.loadWishlistPage();
        }
    }

    async syncWishlist(action, data = null) {
        if (this.DEBUG) {
            console.group('[Wishlist] Sincronizando con el servidor');
            console.log('Acción:', action);
            console.log('Datos enviados:', JSON.stringify(data, null, 2));
            console.log('Customer ID:', this.config.customerId);
            console.log('App Proxy URL:', this.config.appProxyUrl);
        }
        try {
            if (!this.config.customerId && action !== 'get') {
                throw new Error('customerId no está definido. Verifica la configuración en theme.liquid.');
            }
            const payload = {
                action,
                customerId: this.config.customerId || 'anonymous',
                namespace: 'custom',
                key: 'wishlist'
            };
            if (action === 'set' && data) {
                payload.data = { ...data, updated_at: new Date().toISOString() };
            }
            if (this.DEBUG) {
                console.log('Payload:', JSON.stringify(payload, null, 2));
            }
            const token = this.generateJWT();
            const response = await fetch(this.config.appProxyUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (this.DEBUG) {
                    console.error('Respuesta del servidor:', {
                        status: response.status,
                        statusText: response.statusText,
                        body: errorText
                    });
                }
                throw new Error(`Error HTTP: ${response.status}, Respuesta: ${errorText}`);
            }
            const result = await response.json();
            if (this.DEBUG) {
                console.log('Respuesta exitosa:', JSON.stringify(result, null, 2));
                console.groupEnd();
            }
            return result;
        } catch (error) {
            if (this.DEBUG) {
                console.error('Error en syncWishlist:', {
                    message: error.message,
                    stack: error.stack
                });
                console.groupEnd();
            }
            throw error;
        }
    }

    setupEventListeners() {
        if (this.DEBUG) console.log('[Wishlist] Configurando event listeners...');
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.acon_skinko_wishlist_btn');
            if (btn) {
                if (this.DEBUG) console.log('[Wishlist] Click en botón de wishlist:', btn.dataset);
                this.handleWishlistClick(btn);
            }
            if (e.target.classList.contains('skinko-close-btn')) this.closeModal();
            if (e.target.id === 'skinko-new-wishlist-btn') this.showCreateFolderForm();
            if (e.target.id === 'skinko-create-folder-btn') this.createFolderFromModal();
            if (e.target.id === 'skinko-add-to-list-btn') this.addToSelectedFolder();
            const listCard = e.target.closest('.skinko-wishlist-list-card');
            if (listCard) {
                const folderName = listCard.dataset.folder;
                if (this.DEBUG) console.log('[Wishlist] Clic en Ver Lista:', folderName);
                window.history.pushState({}, '', `?folder=${encodeURIComponent(folderName)}`);
                this.loadWishlistPage();
            }
            const backBtn = e.target.closest('.skinko-back-btn');
            if (backBtn) {
                if (this.DEBUG) console.log('[Wishlist] Clic en Back');
                window.history.pushState({}, '', '?folder=all');
                this.loadWishlistPage();
            }
            const deleteBtn = e.target.closest('.skinko-delete-btn');
            if (deleteBtn) {
                const productId = deleteBtn.dataset.productId;
                if (this.DEBUG) console.log('[Wishlist] Clic en Eliminar producto:', productId);
                this.removeFromWishlist(productId).then(() => {
                    this.saveToLocalStorage();
                    this.enqueueSync();
                    this.loadWishlistPage();
                });
            }
            const addToCartBtn = e.target.closest('.skinko-add-to-cart-btn');
            if (addToCartBtn) {
                const variantId = addToCartBtn.dataset.variantId;
                if (this.DEBUG) console.log('[Wishlist] Clic en Agregar al carrito:', variantId);
                this.addToCart(variantId);
            }
        });
        document.addEventListener('change', (e) => {
            if (e.target.classList.contains('skinko-wishlist-checkbox')) {
                const selectedCount = document.querySelectorAll('.skinko-wishlist-checkbox:checked').length;
                const addButton = document.getElementById('skinko-add-to-list-btn');
                if (addButton) {
                    addButton.disabled = selectedCount === 0;
                }
                if (this.DEBUG) console.log('[Wishlist] Checkbox cambiado:', { folder: e.target.dataset.folder, checked: e.target.checked });
            }
        });
        window.addEventListener('storage', (e) => {
            if (e.key === 'acon_skinko_wishlist' && e.newValue) {
                const newData = JSON.parse(e.newValue);
                if (newData && typeof newData === 'object' && newData.folders && typeof newData.folders === 'object') {
                    if (JSON.stringify(this.wishlist) !== JSON.stringify(newData)) {
                        this.wishlist = newData;
                        this.updateWishlistProductSet();
                        this.updateAllButtons();
                        if (document.querySelector('.acon_skinko_wishlist_page')) {
                            this.renderWishlist(this.wishlist);
                        }
                    }
                }
            }
        });
        const createFolderBtn = document.getElementById('acon_skinko_create_folder');
        if (createFolderBtn) {
            createFolderBtn.addEventListener('click', () => {
                const folderInput = document.getElementById('acon_skinko_new_folder');
                if (folderInput && folderInput.value.trim()) {
                    this.createFolder(folderInput.value.trim());
                }
            });
        }
    }

    async handleWishlistClick(btn) {
        if (this.DEBUG) console.log('[Wishlist] Manejando click en wishlist:', btn.dataset);
        const productId = btn.dataset.productId;
        if (!productId) {
            if (this.DEBUG) console.error('[Wishlist] productId vacío en el botón:', btn);
            alert('Error: No se pudo identificar el producto. Contacta al soporte.');
            return;
        }
        const heart = btn.querySelector('.acon_skinko_heart');
        const inWishlist = this.isProductInWishlist(productId);
        if (inWishlist) {
            if (this.DEBUG) console.log('[Wishlist] Eliminando producto de la wishlist:', productId);
            await this.removeFromWishlist(productId);
            heart.classList.remove('filled');
            heart.classList.add('empty');
            try {
                await this.updateWishlistCount(productId, -1);
            } catch (error) {
                if (this.DEBUG) console.warn('[Wishlist] Error al actualizar contador (ignorado):', error);
            }
        } else {
            this.currentProduct = {
                id: productId,
                title: btn.dataset.productTitle || 'Producto sin título',
                image: btn.dataset.productImage || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif',
                handle: btn.dataset.productHandle || ''
            };
            if (this.DEBUG) console.log('[Wishlist] Mostrando modal para producto:', this.currentProduct);
            if (this.config.customerLoggedIn) {
                this.showModal();
            } else {
                if (this.DEBUG) console.log('[Wishlist] Usuario no autenticado, agregando a localStorage');
                this.addToWishlist(productId);
                this.saveToLocalStorage();
                heart.classList.remove('empty');
                heart.classList.add('filled');
            }
        }
        if (this.config.customerLoggedIn) {
            this.saveToLocalStorage();
            this.enqueueSync();
        }
    }

    showModal() {
        if (!this.currentProduct) {
            if (this.DEBUG) console.error('[Wishlist] No hay producto actual para mostrar en el modal');
            return;
        }
        if (this.DEBUG) console.log('[Wishlist] Mostrando modal para:', this.currentProduct);
        const modal = document.getElementById('acon_skinko_wishlist_modal');
        if (!modal) {
            if (this.DEBUG) console.error('[Wishlist] Elemento acon_skinko_wishlist_modal no encontrado');
            return;
        }
        const titleElement = document.getElementById('skinko-modal-product-title');
        if (!titleElement) {
            if (this.DEBUG) console.error('[Wishlist] Elemento skinko-modal-product-title no encontrado');
            return;
        }
        titleElement.textContent = this.currentProduct.title;
        this.renderFolderOptions();
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
    }

    closeModal() {
        if (this.DEBUG) console.log('[Wishlist] Cerrando modal');
        const modal = document.getElementById('acon_skinko_wishlist_modal');
        if (modal) modal.style.display = 'none';
        document.body.style.overflow = '';
        this.currentProduct = null;
    }

    renderFolderOptions() {
        if (this.DEBUG) console.log('[Wishlist] Renderizando opciones de carpetas');
        const container = document.getElementById('skinko-wishlist-container');
        if (!container) {
            if (this.DEBUG) console.error('[Wishlist] Elemento skinko-wishlist-container no encontrado');
            return;
        }
        container.innerHTML = '';
        Object.entries(this.wishlist.folders).forEach(([folderName, products]) => {
            const folderElement = document.createElement('div');
            folderElement.className = 'skinko-wishlist-item skinko-value skinko-is-button skinko-color-2 skinko-hover-color-1';
            folderElement.setAttribute('role', 'checkbox');
            folderElement.setAttribute('aria-checked', 'false');
            folderElement.setAttribute('aria-label', `Agregar a ${folderName === 'default' ? 'Mi lista de deseos' : folderName} (${products.length})`);
            folderElement.innerHTML = `
                <input type="checkbox" class="skinko-wishlist-checkbox" data-folder="${folderName}">
                <span class="skinko-wishlist-text">
                    <span class="skinko-wishlist-name">${folderName === 'default' ? 'Mi lista de deseos' : folderName}</span>
                    <span class="skinko-wishlist-count">(${products.length})</span>
                </span>
            `;
            container.appendChild(folderElement);
        });
    }

    showCreateFolderForm() {
        if (this.DEBUG) console.log('[Wishlist] Mostrando formulario para crear carpeta');
        const container = document.getElementById('skinko-wishlist-container');
        if (!container) {
            if (this.DEBUG) console.error('[Wishlist] Elemento skinko-wishlist-container no encontrado');
            return;
        }
        container.innerHTML = `
            <div class="skinko-create-folder-form">
                <input type="text" id="skinko-new-folder-name" placeholder="Nombre de la lista">
                <button id="skinko-create-folder-btn" class="skinko-button skinko-button-2 skinko-color-2">Crear</button>
            </div>
        `;
    }

    createFolderFromModal() {
        if (this.DEBUG) console.log('[Wishlist] Creando carpeta desde modal');
        const container = document.getElementById('skinko-wishlist-container');
        if (!container) {
            if (this.DEBUG) console.error('[Wishlist] Elemento skinko-wishlist-container no encontrado');
            return;
        }
        const input = document.getElementById('skinko-new-folder-name');
        if (!input) {
            if (this.DEBUG) console.error('[Wishlist] Elemento skinko-new-folder-name no encontrado');
            return;
        }
        const folderName = input.value.trim();
        if (!folderName) {
            if (this.DEBUG) console.warn('[Wishlist] Nombre de carpeta vacío');
            alert('Por favor ingresa un nombre para la lista.');
            return;
        }
        if (this.wishlist.folders[folderName]) {
            if (this.DEBUG) console.warn('[Wishlist] Carpeta ya existe:', folderName);
            alert('Ya existe una lista con ese nombre.');
            return;
        }
        this.wishlist.folders[folderName] = [];
        this.saveToLocalStorage();
        if (this.config.customerLoggedIn) {
            this.enqueueSync();
        }
        this.renderFolderOptions();
        if (this.DEBUG) console.log('[Wishlist] Carpeta creada exitosamente:', folderName);
    }

    async addToSelectedFolder() {
        if (!this.currentProduct) {
            if (this.DEBUG) console.error('[Wishlist] No hay producto seleccionado');
            alert('Error: No hay producto seleccionado.');
            return;
        }
        const selectedCheckboxes = document.querySelectorAll('.skinko-wishlist-checkbox:checked');
        if (selectedCheckboxes.length === 0) {
            if (this.DEBUG) console.warn('[Wishlist] No se seleccionó ninguna lista');
            alert('Por favor selecciona al menos una lista.');
            return;
        }
        const productIdString = String(this.currentProduct.id);
        let added = false;
        for (const checkbox of selectedCheckboxes) {
            const folderName = checkbox.dataset.folder;
            if (this.addToWishlist(productIdString, folderName)) {
                added = true;
            }
        }
        if (added) {
            this.saveToLocalStorage();
            if (this.config.customerLoggedIn) {
                try {
                    document.querySelectorAll(`.acon_skinko_wishlist_btn[data-product-id="${productIdString}"] .acon_skinko_heart`).forEach(heart => {
                        heart.classList.remove('empty');
                        heart.classList.add('filled');
                    });
                    const setResult = await this.syncWishlist('set', this.wishlist);
                    if (this.DEBUG) console.log('[Wishlist] Resultado de set en addToSelectedFolder:', JSON.stringify(setResult, null, 2));
                    await this.updateWishlistCount(productIdString, 1);
                    this.closeModal();
                } catch (error) {
                    if (this.DEBUG) console.error('[Wishlist] Error al sincronizar en addToSelectedFolder:', { message: error.message, stack: error.stack });
                    alert('Error al sincronizar con el servidor. Se guardó localmente.');
                    this.showSyncError();
                    this.enqueueSync();
                }
            } else {
                document.querySelectorAll(`.acon_skinko_wishlist_btn[data-product-id="${productIdString}"] .acon_skinko_heart`).forEach(heart => {
                    heart.classList.remove('empty');
                    heart.classList.add('filled');
                });
                this.closeModal();
                alert('Producto añadido a tu lista de deseos localmente. Inicia sesión para sincronizar.');
            }
        }
    }

    async cleanInvalidFolders() {
        if (this.DEBUG) console.log('[Wishlist] Limpiando carpetas inválidas');
        const validFolders = Object.keys(this.wishlist.folders)
            .filter(folder => folder && folder !== '99' && folder !== 'ii' && folder.trim() !== '')
            .reduce((obj, folder) => {
                obj[folder] = this.wishlist.folders[folder];
                return obj;
            }, {});
        this.wishlist.folders = validFolders;
        this.saveToLocalStorage();
        if (this.config.customerLoggedIn) {
            try {
                await this.syncWishlist('set', this.wishlist);
                if (this.DEBUG) console.log('[Wishlist] Carpetas inválidas limpiadas y sincronizadas');
            } catch (error) {
                if (this.DEBUG) console.error('[Wishlist] Error al sincronizar tras limpiar carpetas:', error);
                this.showSyncError();
            }
        }
    }

    enqueueSync() {
        if (this.DEBUG) console.log('[Wishlist] Encolando sincronización');
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.processQueue(), this.debounceTime);
    }

    async processQueue() {
        if (this.isSaving || this.queue.length === 0) {
            if (this.DEBUG) console.log('[Wishlist] Sincronización omitida:', { isSaving: this.isSaving, queueLength: this.queue.length });
            return;
        }
        this.isSaving = true;
        this.retryCount = 0;
        if (this.DEBUG) console.log('[Wishlist] Procesando cola de sincronización');
        try {
            await this.syncWishlist('set', this.wishlist);
            this.queue = [];
            if (this.DEBUG) console.log('[Wishlist] Sincronización exitosa');
        } catch (error) {
            if (this.retryCount < this.maxRetries) {
                this.retryCount++;
                if (this.DEBUG) console.log('[Wishlist] Reintentando sincronización:', { retryCount: this.retryCount });
                setTimeout(() => this.processQueue(), 2000 * this.retryCount);
            } else {
                if (this.DEBUG) console.error('[Wishlist] Máximo de reintentos alcanzado');
                this.showSyncError();
                this.queue = [];
            }
        } finally {
            this.isSaving = false;
        }
    }

    loadFromLocalStorage() {
        try {
            const saved = localStorage.getItem('acon_skinko_wishlist');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && parsed.folders && typeof parsed.folders === 'object') {
                    this.wishlist = parsed;
                } else {
                    this.wishlist = { folders: { default: [] } };
                }
            }
        } catch (e) {
            if (this.DEBUG) console.error('[Wishlist] Error al cargar desde localStorage:', e);
            this.wishlist = { folders: { default: [] } };
        }
        this.updateWishlistProductSet();
    }

    saveToLocalStorage() {
        if (this.DEBUG) console.log('[Wishlist] Guardando en localStorage:', JSON.stringify(this.wishlist, null, 2));
        localStorage.setItem('acon_skinko_wishlist', JSON.stringify(this.wishlist));
        this.updateWishlistProductSet();
        window.dispatchEvent(new Event('storage'));
    }

    updateWishlistProductSet() {
        this.wishlistProductSet = new Set(
            Object.values(this.wishlist.folders).flatMap(products => products.map(String))
        );
        if (this.DEBUG) console.log('[Wishlist] Conjunto de productos en wishlist actualizado:', Array.from(this.wishlistProductSet));
    }

    isProductInWishlist(productId) {
        const inWishlist = this.wishlistProductSet.has(String(productId));
        if (this.DEBUG) console.log('[Wishlist] Verificando si el producto está en la wishlist:', { productId, inWishlist });
        return inWishlist;
    }

    addToWishlist(productId, folder = 'default') {
        if (!this.wishlist.folders[folder]) this.wishlist.folders[folder] = [];
        if (this.wishlist.folders[folder].length >= 100) {
            if (this.DEBUG) console.warn('[Wishlist] Límite de 100 productos alcanzado en la carpeta:', folder);
            alert('La lista ha alcanzado el límite de 100 productos.');
            return false;
        }
        if (!this.wishlist.folders[folder].includes(productId)) {
            this.wishlist.folders[folder].push(productId);
            this.updateWishlistProductSet();
            if (this.DEBUG) console.log('[Wishlist] Producto añadido a la carpeta:', { productId, folder });
            return true;
        }
        if (this.DEBUG) console.log('[Wishlist] Producto ya está en la carpeta:', { productId, folder });
        return false;
    }

    async removeFromWishlist(productId) {
        let removed = false;
        for (const folder in this.wishlist.folders) {
            const index = this.wishlist.folders[folder].indexOf(productId);
            if (index !== -1) {
                this.wishlist.folders[folder].splice(index, 1);
                removed = true;
                if (this.DEBUG) console.log('[Wishlist] Producto eliminado de carpeta:', { productId, folder });
            }
        }
        if (!removed) {
            if (this.DEBUG) console.warn('[Wishlist] Producto no encontrado en ninguna carpeta para eliminar:', productId);
        }
        this.updateWishlistProductSet();
        if (this.config.customerLoggedIn) {
            this.saveToLocalStorage();
            await this.syncWishlist('set', this.wishlist);
        }
        return removed;
    }

    updateAllButtons() {
        if (this.DEBUG) console.log('[Wishlist] Actualizando botones de wishlist');
        document.querySelectorAll('.acon_skinko_wishlist_btn').forEach(btn => {
            const productId = btn.dataset.productId;
            const heart = btn.querySelector('.acon_skinko_heart');
            const countElement = btn.nextElementSibling;
            if (this.isProductInWishlist(productId)) {
                heart.classList.add('filled');
                heart.classList.remove('empty');
            } else {
                heart.classList.add('empty');
                heart.classList.remove('filled');
            }
            if (countElement && countElement.classList.contains('wishlist__count')) {
                const productCount = parseInt(this.productCache[productId]?.wishlist_count || 0);
                countElement.textContent = productCount;
                countElement.style.display = productCount > 0 ? 'block' : 'none';
                if (this.DEBUG) console.log('[Wishlist] Actualizando contador:', { productId, count: productCount });
            }
        });
    }

    async syncWithServer() {
        if (this.isSyncing) {
            if (this.DEBUG) console.log('[Wishlist] Sincronización omitida: ya en curso');
            return;
        }
        this.isSyncing = true;
        try {
            const localWishlist = JSON.stringify(this.wishlist);
            if (this.DEBUG) console.log('[Wishlist] Local wishlist antes de sincronizar:', localWishlist);
            if (this.config.customerLoggedIn) {
                const serverData = await this.syncWishlist('get');
                if (this.DEBUG) console.log('[Wishlist] Datos del servidor:', JSON.stringify(serverData, null, 2));
                if (serverData && serverData.folders && typeof serverData.folders === 'object') {
                    const serverHasProducts = Object.values(serverData.folders).some(products => products.length > 0);
                    if (!serverHasProducts && Object.values(this.wishlist.folders).some(products => products.length > 0)) {
                        if (this.DEBUG) console.log('[Wishlist] Servidor vacío, sincronizando datos de localStorage');
                        await this.syncWishlist('set', this.wishlist);
                    } else {
                        this.wishlist = serverData;
                    }
                } else {
                    if (this.DEBUG) console.log('[Wishlist] Servidor no tiene datos válidos, usando localStorage');
                    if (Object.values(this.wishlist.folders).some(products => products.length > 0)) {
                        await this.syncWishlist('set', this.wishlist);
                    }
                }
                this.saveToLocalStorage();
                this.updateWishlistProductSet();
                this.updateAllButtons();
                if (document.querySelector('.acon_skinko_wishlist_page')) {
                    await this.loadWishlistPage();
                }
                if (this.DEBUG) console.log('[Wishlist] Wishlist actualizada con datos del servidor');
            } else {
                if (this.DEBUG) console.log('[Wishlist] Usuario no autenticado, manteniendo datos en localStorage');
            }
        } catch (error) {
            if (this.DEBUG) console.error('[Wishlist] Error en syncWithServer:', error);
            this.showSyncError();
        } finally {
            this.isSyncing = false;
        }
    }

    showSyncError() {
        if (this.DEBUG) console.log('[Wishlist] Mostrando error de sincronización');
        const errorEl = document.getElementById('acon_skinko_sync_error');
        if (errorEl) {
            errorEl.textContent = 'Error al sincronizar la lista de deseos. Intenta de nuevo.';
            errorEl.style.display = 'block';
            setTimeout(() => errorEl.style.display = 'none', 5000);
        }
    }

    async loadWishlistPage() {
        if (this.DEBUG) console.log('[Wishlist] Cargando página de wishlist');
        try {
            if (this.config.customerLoggedIn) {
                const wishlist = await this.syncWishlist('get');
                if (!wishlist || !wishlist.folders) {
                    if (this.DEBUG) console.warn('[Wishlist] Datos de wishlist inválidos:', wishlist);
                    this.wishlist = { folders: { default: [] } };
                    this.saveToLocalStorage();
                    this.renderWishlist(this.wishlist);
                    return;
                }
                this.wishlist = wishlist;
                this.saveToLocalStorage();
                this.updateWishlistProductSet();
                if (this.DEBUG) console.log('[Wishlist] Wishlist cargada:', JSON.stringify(this.wishlist, null, 2));
            }
            await this.renderWishlist(this.wishlist);
        } catch (error) {
            if (this.DEBUG) console.error('[Wishlist] Error al cargar página de wishlist:', error);
            this.showSyncError();
            this.renderWishlist(this.wishlist);
        }
    }

    async renderWishlist(wishlist) {
        if (this.DEBUG) console.log('[Wishlist] Renderizando wishlist:', JSON.stringify(wishlist, null, 2));
        const container = document.querySelector('.acon_skinko_wishlist_products');
        if (!container) {
            if (this.DEBUG) console.error('[Wishlist] Elemento acon_skinko_wishlist_products no encontrado. Verifica page.wishlist.liquid.');
            return;
        }
        const foldersContainer = document.querySelector('.acon_skinko_wishlist_folders');
        if (!foldersContainer) {
            if (this.DEBUG) console.error('[Wishlist] Elemento acon_skinko_wishlist_folders no encontrado. Verifica page.wishlist.liquid.');
        } else {
            this.renderFolders(wishlist.folders, 'all');
        }
        container.innerHTML = '<div class="acon_skinko_loading">Cargando listas...</div>';
        const folderKeys = Object.keys(wishlist.folders).filter(folder => folder && folder !== '99' && folder !== 'ii' && folder.trim() !== '');
        if (folderKeys.length === 0) {
            container.innerHTML = '<p>No hay listas de deseos creadas.</p>';
            if (this.DEBUG) console.log('[Wishlist] No hay carpetas válidas para renderizar');
            return;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const currentFolder = urlParams.get('folder') || 'all';
        if (this.DEBUG) console.log('[Wishlist] Carpeta actual:', currentFolder);
        if (currentFolder !== 'all') {
            const productIds = wishlist.folders[currentFolder] || [];
            if (this.DEBUG) console.log('[Wishlist] Product IDs para la carpeta:', productIds);
            if (productIds.length === 0) {
                container.innerHTML = `<p>No hay productos en la lista "${currentFolder === 'default' ? 'Mi lista de deseos' : currentFolder}".</p>`;
                return;
            }
            const page = parseInt(urlParams.get('page')) || 1;
            const perPage = 12;
            const start = (page - 1) * perPage;
            const end = start + perPage;
            const paginatedIds = productIds.slice(start, end);
            if (this.DEBUG) console.log('[Wishlist] IDs paginados:', paginatedIds);
            try {
                const products = await this.fetchProducts(paginatedIds);
                if (this.DEBUG) console.log('[Wishlist] Productos obtenidos:', products);
                const productElements = [];
                for (const productId of paginatedIds) {
                    const product = products.find(p => p.id.toString() === productId);
                    if (product) {
                        productElements.push(this.createProductElement(product));
                        if (this.DEBUG) console.log('[Wishlist] Producto renderizado desde fetchProducts:', productId);
                    } else {
                        if (this.DEBUG) console.warn('[Wishlist] Producto no encontrado en fetchProducts, intentando respaldo:', productId);
                        const fallbackProduct = await this.getFallbackProductData(productId);
                        productElements.push(this.createProductElement(fallbackProduct));
                    }
                }
                container.innerHTML = `
                    <div class="skinko-wishlist-detail-content">
                        <div class="skinko-action-bar">
                            <button class="skinko-back-btn skinko-nav skinko-nav-1 skinko-is-button" aria-label="Back">
                                <span class="skinko-icon"></span>Volver
                            </button>
                        </div>
                        <div id="acon_skinko_sync_error" style="display: none; color: red;"></div>
                        <ul class="skinko-wishlist-selector">
                            ${productElements.join('')}
                        </ul>
                        ${productIds.length > perPage ? this.renderPagination(productIds.length, page, perPage, currentFolder) : ''}
                    </div>
                `;
            } catch (error) {
                if (this.DEBUG) console.error('[Wishlist] Error al renderizar productos:', error);
                container.innerHTML = '<p>Error cargando productos. Intenta recargar la página.</p>';
            }
        } else {
            const allProductIds = [...new Set(folderKeys.flatMap(folder => wishlist.folders[folder]))];
            if (this.DEBUG) console.log('[Wishlist] Todos los productIds para precarga:', allProductIds);
            if (allProductIds.length > 0) {
                try {
                    await this.fetchProducts(allProductIds);
                    if (this.DEBUG) console.log('[Wishlist] Productos precargados en caché:', Object.keys(this.productCache));
                } catch (error) {
                    if (this.DEBUG) console.error('[Wishlist] Error al precargar productos:', error);
                }
            }
            container.innerHTML = `
                <div class="skinko-wishlist-container-content">
                    <div class="skinko-wishlist-list">
                        <div class="skinko-wishlist-selector">
                            ${folderKeys.slice(0, 4).map(folderName => this.renderWishlistCard(folderName, wishlist.folders[folderName])).join('')}
                        </div>
                    </div>
                </div>
            `;
            this.applyWishlistGridStyles(folderKeys.length);
            if (this.DEBUG) console.log('[Wishlist] Tarjetas de listas renderizadas:', folderKeys.slice(0, 4));
        }
    }

    async getFallbackProductData(productId) {
        if (this.DEBUG) console.group('[Wishlist] Intentando obtener datos de respaldo para producto:', productId);
        let productData = this.productCache[productId];
        if (productData) {
            if (this.DEBUG) {
                console.log('[Wishlist] Método 1: Datos encontrados en productCache:', productData);
                console.groupEnd();
            }
            return productData;
        }

        // Método 2: Usar datos de currentProduct
        if (this.currentProduct && this.currentProduct.id === productId) {
            productData = {
                id: productId,
                title: this.currentProduct.title || 'Producto sin título',
                image: this.currentProduct.image || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif',
                url: this.currentProduct.handle ? `/products/${this.currentProduct.handle}` : '#',
                variants: [],
                wishlist_count: this.productCache[productId]?.wishlist_count || 0,
                handle: this.currentProduct.handle || ''
            };
            this.productCache[productId] = productData;
            if (this.DEBUG) {
                console.log('[Wishlist] Método 2 exitoso: Usando datos de currentProduct:', productData);
                console.groupEnd();
            }
            return productData;
        } else {
            if (this.DEBUG) console.warn('[Wishlist] Método 2 fallido: No hay datos en currentProduct para productId:', productId);
        }

        // Método 3: Usar API Storefront de Shopify
        if (this.config.account?.storefrontAccessToken && this.config.account?.shopDomain) {
            try {
                if (this.DEBUG) console.log('[Wishlist] Método 3: Intentando API Storefront con productId:', productId);
                const query = `
                    query {
                        product(id: "gid://shopify/Product/${productId}") {
                            id
                            title
                            handle
                            images(first: 1) {
                                edges {
                                    node {
                                        src
                                    }
                                }
                            }
                            variants(first: 1) {
                                edges {
                                    node {
                                        id
                                        price
                                        available
                                    }
                                }
                            }
                        }
                    }
                `;
                const response = await fetch(`https://${this.config.account.shopDomain}/api/2025-04/graphql.json`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': this.config.account.storefrontAccessToken
                    },
                    body: JSON.stringify({ query })
                });
                if (response.ok) {
                    const data = await response.json();
                    const product = data.data.product;
                    if (product) {
                        const image = product.images.edges[0]?.node.src || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif';
                        const variants = product.variants.edges.map(edge => ({
                            id: edge.node.id.split('/').pop(),
                            price: edge.node.price,
                            available: edge.node.available
                        }));
                        productData = {
                            id: productId,
                            title: product.title,
                            image: image,
                            url: `/products/${product.handle}`,
                            variants: variants,
                            wishlist_count: this.productCache[productId]?.wishlist_count || 0,
                            handle: product.handle
                        };
                        this.productCache[productId] = productData;
                        if (this.DEBUG) {
                            console.log('[Wishlist] Método 3 exitoso: Datos obtenidos de API Storefront:', productData);
                            console.groupEnd();
                        }
                        return productData;
                    } else {
                        if (this.DEBUG) console.warn('[Wishlist] Método 3 fallido: Producto no encontrado en API Storefront para productId:', productId);
                    }
                } else {
                    if (this.DEBUG) console.warn('[Wishlist] Método 3 fallido: Respuesta no OK para API Storefront:', response.status);
                }
            } catch (error) {
                if (this.DEBUG) console.warn('[Wishlist] Método 3 fallido: Error al obtener datos de API Storefront:', error);
            }
        } else {
            if (this.DEBUG) console.warn('[Wishlist] Método 3 no intentado: storefrontAccessToken o shopDomain no definidos');
        }

        // Método 4: Intentar obtener datos desde /products/{handle}.json
        const btn = document.querySelector(`.acon_skinko_wishlist_btn[data-product-id="${productId}"]`);
        let handle = btn?.dataset.productHandle;
        if (handle) {
            try {
                if (this.DEBUG) console.log('[Wishlist] Método 4: Intentando /products/{handle}.json con handle:', handle);
                const response = await fetch(`/products/${handle}.json`);
                if (response.ok) {
                    const data = await response.json();
                    const product = data.product;
                    if (product && product.id.toString() === productId) {
                        const image = product.images?.[0]?.src || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif';
                        const variants = product.variants?.filter(variant => variant.available) || [];
                        productData = {
                            id: product.id,
                            title: product.title,
                            image: image,
                            url: `/products/${product.handle}`,
                            variants: variants,
                            wishlist_count: this.productCache[productId]?.wishlist_count || 0,
                            handle: product.handle
                        };
                        this.productCache[productId] = productData;
                        if (this.DEBUG) {
                            console.log('[Wishlist] Método 4 exitoso: Datos obtenidos de /products/{handle}.json:', productData);
                            console.groupEnd();
                        }
                        return productData;
                    } else {
                        if (this.DEBUG) console.warn('[Wishlist] Método 4 fallido: ID no coincide o producto no encontrado para handle:', handle);
                    }
                } else {
                    if (this.DEBUG) console.warn('[Wishlist] Método 4 fallido: Respuesta no OK para /products/{handle}.json:', response.status);
                }
            } catch (error) {
                if (this.DEBUG) console.warn('[Wishlist] Método 4 fallido: Error al obtener /products/{handle}.json:', error);
            }
        } else {
            if (this.DEBUG) console.warn('[Wishlist] Método 4 no intentado: No se encontró productHandle en el botón para productId:', productId);
        }

        // Método 5: Usar datos de btn.dataset como respaldo
        if (btn && btn.dataset.productTitle && btn.dataset.productImage) {
            productData = {
                id: productId,
                title: btn.dataset.productTitle || 'Producto sin título',
                image: btn.dataset.productImage || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif',
                url: btn.dataset.productHandle ? `/products/${btn.dataset.productHandle}` : '#',
                variants: [],
                wishlist_count: this.productCache[productId]?.wishlist_count || 0,
                handle: btn.dataset.productHandle || ''
            };
            this.productCache[productId] = productData;
            if (this.DEBUG) {
                console.log('[Wishlist] Método 5 exitoso: Usando datos de btn.dataset:', productData);
                console.groupEnd();
            }
            return productData;
        } else {
            if (this.DEBUG) console.warn('[Wishlist] Método 5 fallido: No se encontraron datos en btn.dataset para productId:', productId);
        }

        // Último recurso: Producto no disponible
        productData = {
            id: productId,
            title: 'Producto no disponible',
            image: '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif',
            url: '#',
            variants: [],
            wishlist_count: 0,
            handle: '',
            unavailable: true
        };
        this.productCache[productId] = productData;
        if (this.DEBUG) {
            console.log('[Wishlist] Último recurso: Producto marcado como no disponible:', productData);
            console.groupEnd();
        }
        return productData;
    }

    renderWishlistCard(folderName, productIds) {
        if (this.DEBUG) console.log('[Wishlist] Renderizando tarjeta de lista:', { folderName, productIds });
        const products = productIds
            .map(id => this.productCache[id])
            .filter(p => p && p.image !== '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif' && !p.unavailable);
        if (this.DEBUG) console.log('[Wishlist] Productos en caché para la carpeta:', products);
        const images = products.slice(0, 4).map(p => p.image).filter(img => img);
        const imageElements = images.length > 0 ? images.map(img => `
            <div class="skinko-wishlist-image-wrapper">
                <img class="skinko-wishlist-image" src="${img}" alt="Vista previa de producto" style="object-fit: cover;">
            </div>
        `).join('') : `
            <div class="skinko-wishlist-image-wrapper">
                <div class="skinko-wishlist-image-placeholder">No hay imágenes disponibles</div>
            </div>
            ${productIds.length > 1 ? `
            <div class="skinko-wishlist-image-wrapper">
                <div class="skinko-wishlist-image-placeholder">No hay imágenes disponibles</div>
            </div>
            ` : ''}
        `;
        return `
            <button class="skinko-wishlist-list-card skinko-is-button user-list" data-folder="${folderName}">
                <div class="skinko-wishlist-header">
                    <div class="skinko-wishlist-header-info">
                        <div class="skinko-wishlist-name skinko-heading skinko-heading-2" role="heading" aria-level="2">
                            ${folderName === 'default' ? 'Mi lista de deseos' : folderName}
                        </div>
                        <div class="skinko-wishlist-product-count skinko-text skinko-text-1">
                            ${productIds.length} ${productIds.length === 1 ? 'producto' : 'productos'}
                        </div>
                    </div>
                    <div class="skinko-wishlist-link-action skinko-wishlist-link skinko-color-2 skinko-button skinko-button-2" aria-label="Ver Lista" role="button" tabindex="0">
                        Ver Lista
                    </div>
                </div>
                <div class="skinko-wishlist-images" style="grid-template-columns: ${productIds.length === 1 ? '1fr' : 'repeat(2, 1fr)'}">
                    ${imageElements}
                </div>
            </button>
        `;
    }

    renderPagination(totalItems, currentPage, perPage, folderName) {
        const totalPages = Math.ceil(totalItems / perPage);
        if (totalPages <= 1) return '';
        let pagination = '<div class="skinko-wishlist-pagination">';
        for (let i = 1; i <= totalPages; i++) {
            pagination += `
                <a href="?folder=${encodeURIComponent(folderName)}&page=${i}" class="skinko-pagination-link ${i === currentPage ? 'active' : ''}">
                    ${i}
                </a>
            `;
        }
        pagination += '</div>';
        return pagination;
    }

    applyWishlistGridStyles(folderCount) {
        const styleId = 'wishlist-grid-styles';
        let styleEl = document.getElementById(styleId);
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
        }
        const columns = folderCount <= 2 ? 2 : 3;
        styleEl.textContent = `
            .skinko-wishlist-selector {
                display: grid;
                grid-template-columns: repeat(${columns}, 1fr);
                gap: 1.5rem;
                padding: 1rem;
            }
            @media (max-width: 768px) {
                .skinko-wishlist-selector {
                    grid-template-columns: 1fr;
                }
            }
            .skinko-wishlist-list-card {
                background: #fff;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 1rem;
                text-align: center;
                cursor: pointer;
            }
            .skinko-wishlist-images {
                display: grid;
                gap: 0.5rem;
                margin: 0.5rem 0;
            }
            .skinko-wishlist-image-wrapper {
                width: 100%;
                aspect-ratio: 1;
                overflow: hidden;
            }
            .skinko-wishlist-image {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .skinko-wishlist-image-placeholder {
                width: 100%;
                height: 100%;
                background: #f0f0f0;
                border: 1px solid #ddd;
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                font-size: 0.8rem;
                color: #666;
            }
            .skinko-wishlist-pagination {
                display: flex;
                justify-content: center;
                gap: 0.5rem;
                margin-top: 1rem;
            }
            .skinko-pagination-link {
                padding: 0.5rem 1rem;
                border: 1px solid #ddd;
                border-radius: 4px;
                text-decoration: none;
                color: #333;
            }
            .skinko-pagination-link.active {
                background: #007bff;
                color: #fff;
                border-color: #007bff;
            }
            .skinko-wishlist-products {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 1.5rem;
                padding: 1rem;
            }
            @media (max-width: 768px) {
                .skinko-wishlist-products {
                    grid-template-columns: repeat(2, 1fr);
                }
            }
            @media (max-width: 480px) {
                .skinko-wishlist-products {
                    grid-template-columns: 1fr;
                }
            }
            .skinko-wishlist-item-list {
                background: #fff;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 1rem;
                text-align: center;
                position: relative;
            }
            .skinko-wishlist-item-list img {
                width: 100%;
                height: auto;
                object-fit: cover;
                aspect-ratio: 1;
                margin-bottom: 1rem;
            }
            .skinko-unavailable-message {
                color: #888;
                font-size: 0.9rem;
                margin-top: 0.5rem;
            }
        `;
    }

    async fetchProducts(productIds) {
        if (!productIds || productIds.length === 0) {
            if (this.DEBUG) console.log('[Wishlist] fetchProducts: No se proporcionaron productIds');
            return [];
        }
        if (this.DEBUG) console.group('[Wishlist] Obteniendo productos para IDs:', productIds);
        try {
            const products = [];
            let page = 1;
            const limit = 50;
            let hasMoreProducts = true;

            // Método 1: Obtener todas las páginas de /products.json
            while (hasMoreProducts) {
                if (this.DEBUG) console.log(`[Wishlist] Método 1: Intentando /products.json?page=${page}&limit=${limit}`);
                const response = await fetch(`/products.json?page=${page}&limit=${limit}`);
                if (!response.ok) {
                    if (this.DEBUG) console.warn(`[Wishlist] Método 1 fallido: Respuesta no OK para página ${page}:`, response.status);
                    throw new Error(`Error al obtener productos: ${response.status}`);
                }
                const data = await response.json();
                if (this.DEBUG) console.log(`[Wishlist] Respuesta de /products.json?page=${page}:`, JSON.stringify(data, null, 2));
                const pageProducts = data.products || [];
                products.push(...pageProducts);
                if (pageProducts.length < limit) {
                    hasMoreProducts = false;
                } else {
                    page++;
                }
            }

            const foundProductIds = products.map(p => p.id.toString());
            if (this.DEBUG) console.log('[Wishlist] Productos encontrados:', foundProductIds);
            const missingIds = productIds.filter(id => !foundProductIds.includes(id));
            if (missingIds.length > 0) {
                if (this.DEBUG) console.warn('[Wishlist] Productos no encontrados en /products.json:', missingIds);
            }
            for (const product of products) {
                const image = product.images?.[0]?.src || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif';
                const variants = product.variants?.filter(variant => variant.available) || [];
                this.productCache[product.id] = {
                    id: product.id,
                    title: product.title,
                    image: image,
                    url: `/products/${product.handle}`,
                    variants: variants,
                    wishlist_count: this.productCache[product.id]?.wishlist_count || 0,
                    handle: product.handle
                };
                if (this.DEBUG) console.log('[Wishlist] Producto almacenado en caché:', product.id, this.productCache[product.id]);
            }
            if (this.DEBUG) console.log('[Wishlist] Método 1 exitoso: Productos obtenidos:', products.length);
            if (this.DEBUG) console.groupEnd();
            return products.filter(product => productIds.includes(product.id.toString()));
        } catch (error) {
            if (this.DEBUG) {
                console.error('[Wishlist] Error en fetchProducts:', error);
                console.groupEnd();
            }
            return [];
        }
    }

    async getWishlistCount(productId) {
        if (this.DEBUG) console.log('[Wishlist] Obteniendo contador para producto:', productId);
        try {
            const response = await fetch(`${this.config.appProxyUrl}/count`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.config.jwtSecret}`
                },
                body: JSON.stringify({ product_id: productId, increment: 0 })
            });
            if (!response.ok) throw new Error(`Error al obtener contador: ${response.status}`);
            const result = await response.json();
            if (this.DEBUG) console.log('[Wishlist] Contador obtenido:', result);
            return parseInt(result.new_count) || 0;
        } catch (error) {
            if (this.DEBUG) console.error('[Wishlist] Error al obtener contador:', error);
            return 0;
        }
    }

    createProductElement(product) {
        if (this.DEBUG) console.log('[Wishlist] Creando elemento de producto:', product.id, product);
        const productData = this.productCache[product.id] || {
            id: product.id,
            title: product.title || 'Producto no disponible',
            image: product.image || '//cdn.shopify.com/s/files/1/0756/4600/9562/files/no-image-medium.gif',
            url: product.handle ? `/products/${product.handle}` : '#',
            variants: product.variants || [],
            wishlist_count: 0,
            handle: product.handle || '',
            unavailable: product.unavailable || false
        };
        const variant = productData.variants?.[0];
        const price = variant?.price 
            ? (Shopify.formatMoney 
                ? Shopify.formatMoney(variant.price, window.money_format) 
                : `$${variant.price}`)
            : 'No disponible';
        const variantId = variant?.id || '';
        const productTitle = productData.title.replace(/"/g, '&quot;');
        const unavailableMessage = productData.unavailable ? '<p class="skinko-unavailable-message">Este producto no está disponible actualmente. Puedes eliminarlo de tu lista.</p>' : '';
        const html = `
            <li>
                <div aria-labelledby="${productTitle}" role="button" tabindex="0" class="skinko-wishlist-item-list">
                    <span id="${productTitle}" style="display: none;">${productTitle}</span>
                    <button class="skinko-delete-btn skinko-nav skinko-nav-1 skinko-is-button" data-product-id="${productData.id}" aria-label="Delete ${productTitle}">
                        <span class="skinko-icon"></span>
                    </button>
                    <a class="skinko-wishlist-image-product-wrapper" href="${productData.url}" aria-label="View ${productTitle}">
                        <img class="skinko-wishlist-image" src="${productData.image}" alt="${productTitle}">
                    </a>
                    <a class="skinko-wishlist-title-product-wrapper" href="${productData.url}" aria-label="View ${productTitle}">
                        <h2 class="skinko-title skinko-title-1">${productTitle}</h2>
                    </a>
                    <div class="skinko-variant-title skinko-text skinko-title-2 skinko-variant-title-spacer">${variant?.title || ''}</div>
                    <div class="skinko-product-price skinko-text skinko-text-1 active">
                        <div class="skinko-product-final-price skinko-value" aria-label="${price}">${price}</div>
                    </div>
                    <button class="skinko-add-to-cart-btn skinko-button skinko-button-1 skinko-is-button skinko-bg-2 skinko-color-4" data-variant-id="${variantId}" ${variantId ? '' : 'disabled'} aria-label="Add To Cart">
                        ${variantId ? 'Agregar al Carrito' : 'No disponible'}
                    </button>
                    ${unavailableMessage}
                </div>
            </li>
        `;
        if (this.DEBUG) console.log('[Wishlist] HTML generado para producto:', product.id, html);
        return html;
    }

    addToCart(variantId) {
        if (this.DEBUG) console.log('[Wishlist] Agregando al carrito:', variantId);
        if (!variantId || isNaN(parseInt(variantId, 10))) {
            if (this.DEBUG) console.error('[Wishlist] ID de variante no válido:', variantId);
            this.showError('No se puede agregar al carrito: variante no válida');
            return;
        }
        try {
            const product = Object.values(this.productCache).find(p => p.variants.some(v => v.id === parseInt(variantId, 10)));
            if (!product || !product.variants.find(v => v.id === parseInt(variantId, 10)).available) {
                if (this.DEBUG) console.error('[Wishlist] Variante no encontrada o no disponible en productCache:', variantId);
                this.showError('Producto no disponible');
                return;
            }
            Shopify.postLink('/cart/add', {
                method: 'post',
                parameters: {
                    id: parseInt(variantId, 10),
                    quantity: 1,
                    add: 'add'
                }
            });
            if (this.DEBUG) console.log('[Wishlist] Producto enviado al carrito con Shopify.postLink:', variantId);
        } catch (error) {
            if (this.DEBUG) console.error('[Wishlist] Error al agregar al carrito:', error);
            this.showError('Error al agregar al carrito: ' + error.message);
        }
    }

    showError(message) {
        if (this.DEBUG) console.log('[Wishlist] Mostrando mensaje de error:', message);
        const errorContainer = document.querySelector('#acon_skinko_sync_error');
        if (errorContainer) {
            errorContainer.textContent = message;
            errorContainer.style.display = 'block';
            setTimeout(() => {
                errorContainer.style.display = 'none';
            }, 5000);
        } else {
            if (this.DEBUG) console.warn('[Wishlist] No se encontró #acon_skinko_sync_error');
        }
    }

    renderFolders(folders, currentFolder) {
        if (this.DEBUG) console.log('[Wishlist] Renderizando carpetas:', { folders, currentFolder });
        const foldersContainer = document.querySelector('.acon_skinko_wishlist_folders');
        if (!foldersContainer) {
            if (this.DEBUG) console.error('[Wishlist] Elemento acon_skinko_wishlist_folders no encontrado. Verifica page.wishlist.liquid.');
            return;
        }
        foldersContainer.innerHTML = '';
        const allFolder = document.createElement('div');
        allFolder.className = `acon_skinko_wishlist_folder ${currentFolder === 'all' ? 'active' : ''}`;
        allFolder.textContent = 'Todos';
        allFolder.addEventListener('click', () => {
            window.location.search = '';
        });
        foldersContainer.appendChild(allFolder);
        Object.keys(folders)
            .filter(folder => folder && folder !== '99' && folder !== 'ii' && folder.trim() !== '')
            .forEach(folderName => {
                const folderElement = document.createElement('div');
                folderElement.className = `acon_skinko_wishlist_folder ${folderName === currentFolder ? 'active' : ''}`;
                folderElement.textContent = folderName === 'default' ? 'Mi lista de deseos' : folderName;
                folderElement.addEventListener('click', () => {
                    window.location.search = `?folder=${encodeURIComponent(folderName)}`;
                });
                foldersContainer.appendChild(folderElement);
            });
    }

    async createFolder(folderName) {
        if (!folderName) return;
        if (this.DEBUG) console.log('[Wishlist] Creando carpeta:', folderName);
        try {
            if (this.config.customerLoggedIn) {
                const wishlist = await this.syncWishlist('get');
                if (wishlist.folders && wishlist.folders[folderName]) {
                    if (this.DEBUG) console.warn('[Wishlist] Carpeta ya existe:', folderName);
                    alert('Ya existe una carpeta con ese nombre.');
                    return;
                }
                wishlist.folders[folderName] = [];
                await this.syncWishlist('set', wishlist);
                this.wishlist = wishlist;
                this.saveToLocalStorage();
                this.updateWishlistProductSet();
            } else {
                this.wishlist.folders[folderName] = [];
                this.saveToLocalStorage();
            }
            window.location.search = `?folder=${encodeURIComponent(folderName)}`;
        } catch (error) {
            if (this.DEBUG) console.error('[Wishlist] Error al crear carpeta:', error);
            this.showSyncError();
        }
    }

    async updateWishlistCount(productId, increment) {
        if (!productId || isNaN(parseInt(productId, 10))) {
            if (this.DEBUG) console.error('[Wishlist] productId no válido:', productId);
            throw new Error('productId no válido');
        }
        if (this.DEBUG) console.group('[Wishlist] Actualizando contador de wishlist:', { productId, increment });
        try {
            const productIdNumber = parseInt(productId, 10);
            const requestBody = { product_id: productIdNumber, increment };
            if (this.DEBUG) console.log('[Wishlist] Cuerpo de la solicitud:', JSON.stringify(requestBody));
            const response = await fetch(`${this.config.appProxyUrl}/count`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.config.jwtSecret}`
                },
                body: JSON.stringify(requestBody)
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (this.DEBUG) console.error('Error en respuesta:', { status: response.status, body: errorText });
                throw new Error(`Error al actualizar contador: ${response.status}, Respuesta: ${errorText}`);
            }
            const result = await response.json();
            if (result.new_count !== undefined) {
                this.productCache[productId] = this.productCache[productId] || {};
                this.productCache[productId].wishlist_count = result.new_count;
                this.updateAllButtons();
                this.dispatchWishlistCountUpdateEvent(productId, result.new_count);
                if (this.DEBUG) console.log('Contador actualizado:', { productId, newCount: result.new_count });
            }
            if (this.DEBUG) console.groupEnd();
            return result;
        } catch (error) {
            if (this.DEBUG) {
                console.error('Error al actualizar contador:', error);
                console.groupEnd();
            }
            throw error;
        }
    }

    dispatchWishlistCountUpdateEvent(productId, newCount) {
        if (this.DEBUG) console.log('[Wishlist] Despachando evento de actualización de contador:', { productId, newCount });
        const event = new CustomEvent('wishlist:countUpdated', {
            detail: { productId, count: newCount }
        });
        window.dispatchEvent(event);
    }
}

if (!window.AconSkinkoWishlistInstance) {
    window.AconSkinkoWishlistInstance = new AconSkinkoWishlist();
}